
import { useEffect, useState, Suspense } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import FirstTimeOnboarding from "@/components/onboarding/FirstTimeOnboarding";
import { Loader2 } from "lucide-react";

// Storage keys for redirect flags
const REDIRECT_HANDLED_KEY = 'auth_redirect_handled';
const IS_NEW_USER_KEY = 'is_new_user';

const LoadingSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const ErrorDisplay = ({ error }: { error: string }) => (
  <div className="min-h-screen bg-background flex items-center justify-center p-4">
    <div className="text-center space-y-4 max-w-md">
      <h1 className="text-2xl font-bold text-destructive">Oops!</h1>
      <p className="text-muted-foreground">{error}</p>
      <button 
        onClick={() => window.location.reload()}
        className="text-primary hover:underline"
      >
        Try Again
      </button>
    </div>
  </div>
);

const Onboarding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    let isSubscribed = true;

    const handleEmailVerification = async () => {
      // Get the error type from URL if it exists
      const params = new URLSearchParams(window.location.search);
      const errorType = params.get('error_description');
      
      if (errorType) {
        console.error("Email verification error:", errorType);
        setError(errorType);
        setLoading(false);
        return;
      }
    };

    const checkOnboardingStatus = async () => {
      try {
        // Mark that we're on the onboarding page so other components don't redirect
        sessionStorage.setItem(REDIRECT_HANDLED_KEY, 'true');
        
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError) {
          console.error("Auth error:", authError);
          navigate('/auth');
          return;
        }

        if (!user) {
          console.log("No authenticated user found");
          navigate('/auth');
          return;
        }

        console.log("Checking onboarding status for user:", user.id);

        // First check if user is admin
        const { data: isAdmin } = await supabase.rpc('is_admin', {
          user_id: user.id
        });

        // If user is admin, allow access regardless of onboarding status
        if (isAdmin) {
          console.log("Admin access granted");
          if (isSubscribed) setLoading(false);
          return;
        }

        // Check profile status
        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('onboarding_completed')
          .eq('id', user.id)
          .single();

        if (profileError) {
          console.error("Profile error:", profileError);
          if (isSubscribed) {
            setError("Could not load your profile. Please try again.");
            setLoading(false);
          }
          return;
        }

        // Check if new user first, regardless of profile status
        const isNewUser = sessionStorage.getItem(IS_NEW_USER_KEY) === 'true';
        console.log("Is new user:", isNewUser, "Profile completed:", profile?.onboarding_completed);
        
        // If completing onboarding, allow access even for existing users
        if (isSubscribed) setLoading(false);
        
        // If profile is completed and not a new user, redirect home
        if (profile?.onboarding_completed === true && !isNewUser) {
          console.log("Onboarding already completed and not a new user, redirecting to home");
          navigate('/', { replace: true });
          return;
        }
      } catch (err) {
        console.error("Onboarding check error:", err);
        if (isSubscribed) {
          setError("Could not load your profile. Please try again.");
          setLoading(false);
        }
      }
    };

    // First handle any email verification
    handleEmailVerification();
    
    // Then check onboarding status
    checkOnboardingStatus();

    // Set up auth state change listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log("Onboarding auth state change:", event);
      if (!session && event !== 'INITIAL_SESSION') {
        navigate('/auth');
      }
    });

    return () => {
      isSubscribed = false;
      subscription.unsubscribe();
    };
  }, [navigate, toast, location]);

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <FirstTimeOnboarding />
    </Suspense>
  );
};

export default Onboarding;
